import type { ReactNode } from "react";

/** Extract the first sentence (or trimmed prefix) for a takeaway-sized snippet. */
export function firstSentence(text: string, max = 220): string {
  const trimmed = text.trim();
  const m = trimmed.match(/^[\s\S]*?[.!?](?=\s|$)/);
  const s = (m ? m[0] : trimmed).trim();
  return s.length > max ? s.slice(0, max - 1).trimEnd() + "…" : s;
}

export type InlineLink = { match: string; href: string };

/**
 * Render text with inline links: the first occurrence of each `match`
 * substring is wrapped in an anchor (overlapping matches are skipped).
 *
 * English articles use these for outbound citations, which open in a new tab
 * (`external`, the default — editorial endorsement: we vouch for every cited
 * source). Bangla articles use them for same-site links (`external: false`).
 */
export function renderWithLinks(
  text: string,
  links?: InlineLink[],
  { external = true }: { external?: boolean } = {},
): ReactNode | ReactNode[] {
  if (!links || links.length === 0) return text;
  type Range = { start: number; end: number; href: string };
  const ranges: Range[] = [];
  for (const { match, href } of links) {
    if (!match) continue;
    const idx = text.indexOf(match);
    if (idx === -1) continue;
    const end = idx + match.length;
    if (ranges.some((r) => idx < r.end && end > r.start)) continue;
    ranges.push({ start: idx, end, href });
  }
  if (ranges.length === 0) return text;
  ranges.sort((a, b) => a.start - b.start);
  const out: ReactNode[] = [];
  let cursor = 0;
  ranges.forEach((r, i) => {
    if (cursor < r.start) out.push(text.slice(cursor, r.start));
    out.push(
      <a
        key={`l-${i}`}
        href={r.href}
        {...(external ? { target: "_blank", rel: "noopener" } : {})}
        className="text-primary underline-offset-4 hover:underline"
      >
        {text.slice(r.start, r.end)}
      </a>,
    );
    cursor = r.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
